import { useFrame } from '@react-three/fiber';
import { useMemo, useRef } from 'react';
import * as THREE from 'three';
import { LINE_FRAG, LINE_VERT } from '../shaders';
import { toSRGB } from '../toSRGB';
import type { MorphBuffers, MorphRef } from '../types';

export function MorphLines({
  buffers,
  color,
  opacity,
  morphRef,
  opacityRef,
}: {
  buffers: MorphBuffers;
  color: string;
  opacity: number;
  morphRef: MorphRef;
  opacityRef?: MorphRef;
}) {
  const materialRef = useRef<THREE.ShaderMaterial>(null);
  const geometry = useMemo(() => {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(buffers.spherePos, 3));
    g.setAttribute('aFlat', new THREE.BufferAttribute(buffers.flatPos, 3));
    return g;
  }, [buffers]);
  const uniforms = useMemo(
    () => ({
      uMorph: { value: 0 },
      uColor: { value: toSRGB(color) },
      uOpacity: { value: opacity },
    }),
    [color, opacity]
  );
  useFrame(() => {
    const material = materialRef.current;
    if (!material) return;
    material.uniforms.uMorph.value = morphRef.current;
    if (opacityRef) {
      material.uniforms.uOpacity.value = opacityRef.current * opacity;
    }
  });
  return (
    <lineSegments geometry={geometry}>
      <shaderMaterial
        ref={materialRef}
        uniforms={uniforms}
        vertexShader={LINE_VERT}
        fragmentShader={LINE_FRAG}
        transparent={true}
        depthWrite={false}
      />
    </lineSegments>
  );
}
